import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Button,
  Table,
  TableAutocomplete,
  Checkbox,
  Snackbar,
  IconButton,
  Pagination,
} from "@narmi/design_system";
import { STATES } from "cerulean/src/StateSelector";
import Container from "../../Container";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import {
  WIRE_COL_LAYOUT,
  WIRE_COL_VISIBILITY,
  WIRE_READ_ONLY_COL_LAYOUT,
  WIRE_READ_ONLY_COL_VISIBILITY,
} from "./wireRecipientReviewConstants";
import { useWireRecipientReview } from "./useWireRecipientReview";
import type { WireRowEdit } from "./wireRecipientReviewTypes";
import styles from "../CreateBulk/RecipientReviewScreen.module.scss";
import wireStyles from "./WireRecipientReviewScreen.module.scss";

const PAGE_SIZE = 20;

const WireRecipientReviewScreen = () => {
  const navigate = useNavigate();
  const {
    l10n,
    parsedRows,
    newRecipientRows,
    canSaveRecipients,
    rowEdits,
    rowErrors,
    rowRecipients,
    selected,
    saved,
    nextLoading,
    hasAnyErrors,
    errorCategories,
    allSelected,
    someSelected,
    startEditing,
    isEditing,
    handleFieldChange,
    handleFieldBlur,
    handleRegionChange,
    handleToggleSelectAll,
    handleToggleRow,
    handleSaveRecipient,
    handleSaveAll,
    handleClearSelection,
    handleBack,
    handleNext,
    getTagClassName,
    getTagLabel,
    canSaveRow,
  } = useWireRecipientReview();

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(newRecipientRows.length / PAGE_SIZE);
  const paginatedRows = newRecipientRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const pageOffset = (currentPage - 1) * PAGE_SIZE;

  // Redirect to upload screen if no data
  useEffect(() => {
    if (!parsedRows) {
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}/upload`, { replace: true });
    }
  }, [parsedRows, navigate]);

  if (!parsedRows) {
    return null;
  }

  // --- Render a text field cell ---
  const renderTextField = (
    idx: number,
    editField: keyof WireRowEdit,
    errorKey: string,
    placeholder: string
  ) => {
    const edit = rowEdits[idx];
    const errors = rowErrors[idx] ?? {};
    const hasError = Boolean(errors[errorKey]);
    const editing = isEditing(idx, errorKey);

    if (!editing) {
      return (
        <div
          className={`${styles.plainText} ${wireStyles.plainText}`}
          onClick={() => startEditing(idx, errorKey)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              startEditing(idx, errorKey);
            }
          }}
        >
          {edit[editField]}
        </div>
      );
    }

    return (
      <div
        className={hasError ? styles.editableInputError : styles.editableInput}
      >
        <input
          type="text"
          value={edit[editField]}
          placeholder={placeholder}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onChange={(e) => handleFieldChange(idx, editField, e.target.value)}
          onBlur={() => handleFieldBlur(idx, editField)}
        />
      </div>
    );
  };

  return (
    <Container maxWidth="1400px">
      <div className={styles.pageHeader}>
        <h1>{l10n.getString("wire-recipient-review-header")}</h1>
      </div>
      <div className={styles.sectionWrapper}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <span
              className="narmi-icon-user-plus"
              style={{ fontSize: "24px" }}
            />
          </div>
          <div className={styles.sectionTextBlock}>
            <div className={styles.sectionTitle}>
              <span className="fontSize--l fontWeight--bold">
                {l10n.getString("recipient-review-section-title")}
              </span>
              <span className={styles.badge}>{newRecipientRows.length}</span>
            </div>
            <span className="fontColor--secondary">
              {canSaveRecipients
                ? l10n.getString("wire-recipient-review-section-description")
                : l10n.getString(
                    "recipient-review-section-description-no-permission"
                  )}
            </span>
          </div>
        </div>
        {hasAnyErrors && (
          <div className={styles.errorBanner}>
            <span
              className={`narmi-icon-alert-circle ${styles.errorBannerIcon}`}
            />
            <div className={styles.errorBannerContent}>
              <span>{l10n.getString("recipient-review-error-banner")}</span>
              <ul className={styles.errorBannerList}>
                {errorCategories.includes("required") && (
                  <li>
                    {l10n.getString("recipient-review-error-required-fields")}
                  </li>
                )}
                {errorCategories.includes("account_number") && (
                  <li>
                    {l10n.getString("recipient-review-error-invalid-account")}
                  </li>
                )}
                {errorCategories.includes("routing_number") && (
                  <li>
                    {l10n.getString("recipient-review-error-invalid-routing")}
                  </li>
                )}
                {(errorCategories.includes("street_address") ||
                  errorCategories.includes("city") ||
                  errorCategories.includes("region_code") ||
                  errorCategories.includes("postal_code")) && (
                  <li>
                    {l10n.getString(
                      "wire-recipient-review-error-invalid-address"
                    )}
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
        <div className={`${styles.tableWrapper} ${wireStyles.tableWrapper}`}>
          <Table
            colVisibility={
              canSaveRecipients
                ? WIRE_COL_VISIBILITY
                : WIRE_READ_ONLY_COL_VISIBILITY
            }
            colLayout={
              canSaveRecipients ? WIRE_COL_LAYOUT : WIRE_READ_ONLY_COL_LAYOUT
            }
          >
            <Table.Header>
              <Table.Row>
                {canSaveRecipients && (
                  <Table.HeaderCell>
                    <Checkbox
                      checked={allSelected}
                      onChange={handleToggleSelectAll}
                      indeterminate={someSelected}
                    />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>
                  {l10n.getString("recipient-review-col-name")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("wire-recipient-review-col-account")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("wire-recipient-review-col-routing")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("wire-recipient-review-col-street")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("wire-recipient-review-col-city")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("wire-recipient-review-col-region")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("wire-recipient-review-col-postal")}
                </Table.HeaderCell>
                {canSaveRecipients && (
                  <Table.HeaderCell>
                    {l10n.getString("recipient-review-col-action")}
                  </Table.HeaderCell>
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedRows.map((_row, pageIdx) => {
                const idx = pageOffset + pageIdx;
                const edit = rowEdits[idx];
                if (!edit) return null;

                return (
                  <Table.Row key={idx}>
                    {canSaveRecipients && (
                      <Table.Cell>
                        <Checkbox
                          checked={!saved.has(idx) && selected.has(idx)}
                          onChange={() => handleToggleRow(idx)}
                          disabled={saved.has(idx) || !rowRecipients[idx]}
                        />
                      </Table.Cell>
                    )}
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "recipient_name",
                        "name",
                        l10n.getString("recipient-review-placeholder-name")
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "account_number",
                        "account_number",
                        l10n.getString("recipient-review-placeholder-account")
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "routing_number",
                        "routing_number",
                        l10n.getString("recipient-review-placeholder-routing")
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "street_address",
                        "street_address",
                        l10n.getString(
                          "wire-recipient-review-placeholder-street"
                        )
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "city",
                        "city",
                        l10n.getString("wire-recipient-review-placeholder-city")
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <TableAutocomplete
                        label={l10n.getString(
                          "wire-recipient-review-col-region"
                        )}
                        placeholder={l10n.getString(
                          "wire-recipient-review-placeholder-region"
                        )}
                        hasError={Boolean(rowErrors[idx]?.region_code)}
                        inputValue={edit.region_code}
                        onInputChange={(value: string) =>
                          handleFieldChange(idx, "region_code", value)
                        }
                        onChange={(value: string) =>
                          handleRegionChange(idx, value)
                        }
                      >
                        {STATES.map((st) => (
                          <TableAutocomplete.Item
                            key={st.code}
                            value={st.code}
                            searchValue={st.code}
                          >
                            {st.code}
                          </TableAutocomplete.Item>
                        ))}
                      </TableAutocomplete>
                    </Table.Cell>
                    <Table.Cell>
                      {renderTextField(
                        idx,
                        "postal_code",
                        "postal_code",
                        l10n.getString(
                          "wire-recipient-review-placeholder-postal"
                        )
                      )}
                    </Table.Cell>
                    {canSaveRecipients && (
                      <Table.Cell>
                        <button
                          type="button"
                          className={getTagClassName(idx, styles)}
                          onClick={() => handleSaveRecipient(idx)}
                          disabled={!canSaveRow(idx)}
                        >
                          {getTagLabel(idx)}
                        </button>
                      </Table.Cell>
                    )}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          {totalPages > 1 && (
            <div className={styles.paginationWrapper}>
              <Pagination
                totalPages={totalPages}
                selectedPage={currentPage}
                onPageChange={(page: number) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.footerActions}>
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <button
              type="button"
              className={styles.backLink}
              onClick={handleBack}
            >
              {l10n.getString("recipient-review-back")}
            </button>
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={
                nextLoading
                  ? l10n.getString("recipient-review-next-loading")
                  : l10n.getString("button-next")
              }
              disabled={hasAnyErrors || nextLoading}
              onClick={handleNext}
            />
          </Row.Item>
        </Row>
      </div>
      <div className={styles.stickyContainer}>
        <Snackbar isActive={canSaveRecipients && selected.size > 0}>
          <span className="fontColor--primary fontWeight--semibold">
            {l10n.getString("recipient-review-snackbar-selected", {
              count: selected.size,
            })}
          </span>
          <IconButton
            kind="action"
            label={l10n.getString("recipient-review-clear-selection")}
            name="x"
            textSize="l"
            onClick={handleClearSelection}
          />
          <Snackbar.Divider />
          <Snackbar.ButtonGroup>
            <Button
              kind="primary"
              label={l10n.getString("recipient-review-save-all")}
              onClick={handleSaveAll}
            />
          </Snackbar.ButtonGroup>
        </Snackbar>
      </div>
    </Container>
  );
};

export default WireRecipientReviewScreen;
